import React, { useState, useEffect, useMemo } from "react";
import { Global } from "../helpers/Global";
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import * as XLSX from "xlsx";
import Select from "react-select";
import { FingerprintCheckerJornadas } from "./fingerprintJornadas";

// Font Awesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCsv,
  faFileExcel,
  faPenToSquare,
  faAnglesLeft,
  faAngleLeft,
  faAngleRight,
  faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const JornadasAdmin = () => {
  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [centros, setCentros] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [orden, setOrden] = useState({ campo: "fin", ascendente: false });
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio"); // "inicio" | "cierre"
  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);
  const [mensaje, setMensaje] = useState("");

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Modal edición
  const [modalEditar, setModalEditar] = useState(false);
  const [jornadaEditar, setJornadaEditar] = useState(null);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    try {
      const [resUsuarios, resCentros, resJornadas] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}jornadas/finalizadas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const usuariosData = resUsuarios.ok ? await resUsuarios.json() : [];
      const centrosData = resCentros.ok ? await resCentros.json() : [];
      const jornadasData = resJornadas.ok ? await resJornadas.json() : [];

      setUsuarios(usuariosData);
      setCentros(centrosData);

      const enriquecidas = jornadasData.map((j) => {
        const creador = usuariosData.find((u) => u.id === j.creado_por_admin_id);
        const centro = centrosData.find((c) => c.id === j.centro_id);
        return {
          ...j,
          duracionMs: new Date(j.fin) - new Date(j.inicio),
          creado_por_nombre: creador ? `${creador.nombre} ${creador.apellidos || ""}` : "-",
          centro: centro ? centro.nombre : `Centro ${j.centro_id}`,
        };
      });

      setJornadas(enriquecidas);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      setMensaje("Error al cargar los datos iniciales.");
    }
  };

  // ------- Filtros -------
  const usuarioOptions = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...usuarios
        .slice()
        .sort((a, b) => (a.nombre + (a.apellidos || "")).localeCompare(b.nombre + (b.apellidos || "")))
        .map((u) => ({ value: u.id, label: `${u.nombre} ${u.apellidos || ""}`.trim() })),
    ],
    [usuarios]
  );

  const centroOptions = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...centros
        .slice()
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
        .map((c) => ({ value: c.id, label: c.nombre })),
    ],
    [centros]
  );

  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFiltroCentro("");
    setFechaDesde("");
    setFechaHasta("");
  };

  // formatea "dd/mm/aaaa, HH:MM"
  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // dd/mm/aaaa (local)
  const fmtDateOnly = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("es-ES");
  };

  const fmtBoolSN = (b) => (b ? "Sí" : "No");

  // Devuelve nombre del validador (o "-")
  const nombreValidador = (usuarios, id) => {
    if (!id) return "-";
    const u = usuarios.find((x) => String(x.id) === String(id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${id}`;
  };


  const abrirMapa = async (j, focus = "inicio") => {
    setMapFocus(focus);

    const ini = j.latitud && j.longitud ? { lat: Number(j.latitud), lng: Number(j.longitud) } : null;
    const fin =
      j.latitud_cierre && j.longitud_cierre
        ? { lat: Number(j.latitud_cierre), lng: Number(j.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${j.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();

      if (res.ok && data.latitud && data.longitud) {
        setUbicacionCentro({
          lat: parseFloat(data.latitud),
          lng: parseFloat(data.longitud),
        });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }
    setMostrarMapa(true);
  };

  const jornadasFiltradas = useMemo(() => {
    return jornadas.filter((j) => {
      const inicio = new Date(j.inicio).toISOString().split("T")[0];

      const matchUsuario = !filtroUsuario || j.usuario_id?.toString() === filtroUsuario.toString();
      const matchCentro = !filtroCentro || j.centro_id?.toString() === filtroCentro.toString();
      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicio === fechaDesde
          : fechaDesde && fechaHasta
          ? inicio >= fechaDesde && inicio <= fechaHasta
          : true;

      return matchUsuario && matchCentro && matchFecha;
    });
  }, [jornadas, filtroUsuario, filtroCentro, fechaDesde, fechaHasta]);

  const manejarOrden = (campo) => {
    const campoOrden =
      campo === "duracion"
        ? "duracionMs"
        : campo === "distancia_m"
        ? "distancia_m"
        : campo === "distancia_m_cierre"
        ? "distancia_m_cierre"
        : campo === "creada_por_nombre"
        ? "creado_por_nombre"
        : campo === "observaciones_admin"
        ? "observaciones_admin"
        : campo === "usuario"
        ? "usuario"
        : campo === "centro"
        ? "centro"
        : campo === "motivo_fin"
        ? "motivo_fin"
        : campo; // inicio, fin

    setOrden((prev) => ({
      campo: campoOrden,
      ascendente: prev.campo === campoOrden ? !prev.ascendente : true,
    }));
  };

  const jornadasOrdenadas = useMemo(() => {
    return [...jornadasFiltradas].sort((a, b) => {
      let valorA = a[orden.campo] ?? "";
      let valorB = b[orden.campo] ?? "";

      if (orden.campo === "inicio" || orden.campo === "fin") {
        valorA = new Date(valorA);
        valorB = new Date(valorB);
      }

      if (typeof valorA === "string" && typeof valorB === "string") {
        return orden.ascendente
          ? valorA.localeCompare(valorB, undefined, { sensitivity: "base" })
          : valorB.localeCompare(valorA, undefined, { sensitivity: "base" });
      }
      return orden.ascendente ? valorA - valorB : valorB - valorA;
    });
  }, [jornadasFiltradas, orden]);

  // ---- Paginación ----
  useEffect(() => {
    setPage(1);
  }, [filtroUsuario, filtroCentro, fechaDesde, fechaHasta, pageSize, orden]);

  const total = jornadasOrdenadas.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const startIndex = (pageClamped - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const pageItems = useMemo(
    () => jornadasOrdenadas.slice(startIndex, endIndex),
    [jornadasOrdenadas, startIndex, endIndex]
  );

  const calcularDuracion = (ms) => {
    if (!ms || isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  // ------ VALIDACIÓN -------
  const toggleValidada = async (j) => {
    try {
      const res = await fetch(`${Global.url}jornadas/${j.id}/validar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ validada: !j.validada }),
      });

      if (!res.ok) {
        setMensaje(res.status === 403 ? "No tienes permisos para validar esta jornada." : "Error al cambiar el estado de validación.");
        return;
      }

      const updated = await res.json();
      setJornadas((prev) =>
        prev.map((row) =>
          row.id === j.id
            ? { ...row, ...updated, duracionMs: new Date(updated.fin) - new Date(updated.inicio) }
            : row
        )
      );
      setMensaje(updated.validada ? "Jornada validada." : "Validación retirada.");
    } catch (e) {
      console.error(e);
      setMensaje("Error de red al validar/desvalidar la jornada.");
    }
  };

  // ------ EXPORTACIÓN (igual que antes, sin cambios de layout) ------
  const exportarCSVReducido = (datos, nombreArchivo = "jornadas.csv") => {
  if (!Array.isArray(datos) || datos.length === 0) {
    alert("No hay datos para exportar.");
    return;
  }

  const encabezados = [
    "Usuario",
    "Centro",
    "Inicio",
    "Fin",
    "Duración",
    "En Rango (Inicio)",
    "Distancia (Inicio m)",
    "En Rango (Cierre)",
    "Distancia (Cierre m)",
    "Motivo Fin",
    "Creado por",
    "Observaciones",
    // 👇 Campos de validación (nuevos)
    "Validada",
    "Validada por",
    "Validada en",
  ];

  const csvRows = [
    encabezados.join(","),
    ...datos.map((j) => {
      const usuario = usuarios.find((u) => u.id === j.usuario_id);
      const nombreUsuario = usuario ? `${usuario.nombre} ${usuario.apellidos || ""}` : "-";

      const fila = [
        nombreUsuario,
        j.centro ?? "",
        j.inicio ? new Date(j.inicio).toLocaleString("es-ES") : "-",
        j.fin ? new Date(j.fin).toLocaleString("es-ES") : "-",
        calcularDuracion(j.duracionMs),
        j.en_rango ? "En rango" : "Fuera de rango",
        j.distancia_m != null ? j.distancia_m : "",
        j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
        j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
        j.motivo_fin ?? "-",
        j.creado_por_nombre || "-",
        j.observaciones_admin || "-",
        // 👇 Validación (igual que la tabla)
        fmtBoolSN(!!j.validada),
        nombreValidador(usuarios, j.validada_por),
        j.validada ? fmtDate(j.validada_en) : "-",
      ];

      // CSV-escape
      return fila.map((val) => `"${val?.toString().replace(/"/g, '""') ?? ""}"`).join(",");
    }),
  ];

  const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", nombreArchivo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  const exportarExcel = (datos, nombreArchivo = "jornadas.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((j) => {
      const usuario = usuarios.find((u) => u.id === j.usuario_id);
      const nombreUsuario = usuario ? `${usuario.nombre} ${usuario.apellidos || ""}` : "-";

      return {
        Usuario: nombreUsuario,
        Centro: j.centro ?? "",
        Inicio: j.inicio ? new Date(j.inicio).toLocaleString("es-ES") : "-",
        Fin: j.fin ? new Date(j.fin).toLocaleString("es-ES") : "-",
        Duración: calcularDuracion(j.duracionMs),
        "En Rango (Inicio)": j.en_rango ? "En rango" : "Fuera de rango",
        "Distancia (Inicio m)": j.distancia_m != null ? j.distancia_m : "",
        "En Rango (Cierre)": j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
        "Distancia (Cierre m)": j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
        "Motivo Fin": j.motivo_fin ?? "-",
        "Creado por": j.creado_por_nombre || "-",
        Observaciones: j.observaciones_admin || "-",
        // 👇 Validación (idéntico a la tabla)
        Validada: fmtBoolSN(!!j.validada),
        "Validada por": nombreValidador(usuarios, j.validada_por),
        "Validada en": j.validada ? fmtDate(j.validada_en) : "-",
      };
    });

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Jornadas");
    XLSX.writeFile(libro, nombreArchivo);
  };


  // ---------- MODAL EDICIÓN -----------
  const ModalEditar = ({ show, onClose, jornada, onGuardar }) => {
    const [inicio, setInicio] = useState("");
    const [fin, setFin] = useState("");
    const [motivo, setMotivo] = useState("");
    const [observaciones, setObservaciones] = useState("");
    const [error, setError] = useState("");

    function toDatetimeLocalString(date) {
      if (!date) return "";
      const pad = (n) => n.toString().padStart(2, "0");
      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const min = pad(date.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }

    useEffect(() => {
      if (!jornada) return;
      setInicio(jornada.inicio ? toDatetimeLocalString(new Date(jornada.inicio)) : "");
      setFin(jornada.fin ? toDatetimeLocalString(new Date(jornada.fin)) : "");
      setMotivo(jornada.motivo_fin || "");
      setObservaciones(jornada.comentario_admin || "");
      setError("");
    }, [jornada]);

    if (!show || !jornada) return null;

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!inicio || !fin) {
        setError("Debes indicar inicio y fin.");
        return;
      }
      if (!observaciones.trim()) {
        setError("Debes introducir una observación.");
        return;
      }

      const dInicio = new Date(inicio);
      const dFin = new Date(fin);
      if (isNaN(dInicio) || isNaN(dFin) || dFin < dInicio) {
        setError("La fecha de fin debe ser posterior al inicio");
        return;
      }

      await onGuardar({
        id: jornada.id,
        usuario_id: jornada.usuario_id,
        centro_id: jornada.centro_id,
        inicio,
        fin,
        motivo_fin: motivo,
        observaciones_admin: observaciones,
      });
    };

    return (
      <div className="turnos-modal-backdrop">
        <div className="turnos-modal">
          <h3>Modificar jornada</h3>
          <form onSubmit={handleSubmit}>
            <label>
              Inicio:
              <input
                type="datetime-local"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                required
              />
            </label>
            <label>
              Fin:
              <input type="datetime-local" value={fin} onChange={(e) => setFin(e.target.value)} required />
            </label>
            <label>
              Motivo fin:
              <input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo de la modificación"
              />
            </label>
            <label>
              Observaciones:
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Observaciones administrativas"
                required
              />
            </label>
            {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
            <div style={{ marginTop: 16 }}>
              <button type="submit">Guardar cambios</button>
              <button type="button" onClick={onClose} style={{ marginLeft: 8 }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleGuardarEdicion = async (edicion) => {
    try {
      const res = await fetch(`${Global.url}jornadas/modificar/${edicion.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          usuario_id: edicion.usuario_id,
          centro_id: edicion.centro_id,
          inicio: edicion.inicio,
          fin: edicion.fin,
          motivo_fin: edicion.motivo_fin,
          observaciones_admin: edicion.observaciones_admin,
        }),
      });

      if (res.ok) {
        setMensaje("Jornada modificada correctamente.");
        setModalEditar(false);
        setJornadaEditar(null);
        cargarDatos();
      } else {
        setMensaje("Error al modificar la jornada.");
      }
    } catch (e) {
      setMensaje("Error al conectar con el servidor.");
    }
  };

  // --------------- RENDER ------------------
  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Jornadas Finalizadas</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}

      {/* FILTROS */}
      <div className="filtros">
        <div className="filtro">
          <label>Usuario:</label>
          <Select
            options={usuarioOptions}
            value={
              filtroUsuario
                ? {
                    value: filtroUsuario,
                    label: (() => {
                      const u = usuarios.find((u) => String(u.id) === String(filtroUsuario));
                      return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : "";
                    })(),
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroUsuario(selected?.value || "")}
            isClearable
            placeholder="Filtrar por usuario"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Centro:</label>
          <Select
            options={centroOptions}
            value={
              filtroCentro
                ? {
                    value: filtroCentro,
                    label: (() => {
                      const c = centros.find((c) => String(c.id) === String(filtroCentro));
                      return c ? c.nombre : `Centro ${filtroCentro}`;
                    })(),
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroCentro(selected?.value || "")}
            isClearable
            placeholder="Filtrar por centro"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Desde:</label>
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
        </div>

        <div className="filtro">
          <label>Hasta:</label>
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
        </div>

        <div className="filtro" style={{ alignSelf: "end" }}>
          <button className="btn-login" onClick={limpiarFiltros}>
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Paginación superior */}
      <div className="paginacion-bar">
        <div className="paginacion-info">
          Mostrando <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> de{" "}
          <strong>{total}</strong>
        </div>
        <div className="paginacion-controles">
          <button className="btn-mapa" onClick={() => setPage(1)} disabled={pageClamped === 1} title="Primera">
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
          <button
            className="btn-mapa"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pageClamped === 1}
            title="Anterior"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <span className="paginacion-estado">
            Página {pageClamped} / {totalPages}
          </span>
          <button
            className="btn-mapa"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={pageClamped === totalPages}
            title="Siguiente"
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button
            className="btn-mapa"
            onClick={() => setPage(totalPages)}
            disabled={pageClamped === totalPages}
            title="Última"
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>

          <select
            className="paginacion-size"
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            title="Tamaño de página"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / pág.
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-listado-padre">
        <table className="table-listado">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("usuario")}>Usuario</th>
              <th onClick={() => manejarOrden("centro")}>Centro</th>
              <th onClick={() => manejarOrden("inicio")}>Inicio</th>
              <th onClick={() => manejarOrden("fin")}>Fin</th>
              <th onClick={() => manejarOrden("duracion")}>Duración</th>

              {/* Distancias (chips coloreados) */}
              <th className="col-distancia">
                <div className="th-multi">
                  <span onClick={() => manejarOrden("distancia_m")} className="ordenable">Distancia (Inicio)</span>
                  <span onClick={() => manejarOrden("distancia_m_cierre")} className="ordenable th-sub">Distancia (Cierre)</span>
                </div>
              </th>

              {/* Centro del layout */}
              <th onClick={() => manejarOrden("validada")}>Validada</th>
              <th>Acciones</th>

              {/* Derecha (gestión) */}
              <th onClick={() => manejarOrden("validada_por")}>Validado por</th>
              <th onClick={() => manejarOrden("motivo_fin")}>Motivo Fin</th>
              <th onClick={() => manejarOrden("creada_por_nombre")}>Creado por</th>
              <th onClick={() => manejarOrden("observaciones_admin")}>Observaciones</th>
            </tr>
          </thead>

          <tbody>
            {pageItems.map((j) => {
              const validador = j.validada_por ? usuarios.find((u) => u.id === j.validada_por) : null;
              const validadorNombre = validador ? `${validador.nombre} ${validador.apellidos || ""}`.trim() : "-";

              return (
                <tr key={j.id}>
                  <td>
                    {(() => {
                      const u = usuarios.find((u) => u.id === j.usuario_id);
                      return u ? `${u.nombre} ${u.apellidos || ""}` : j.usuario || "-";
                    })()}
                  </td>
                  <td>{j.centro}</td>
                  <td>{fmtDate(j.inicio)}</td>
                  <td>{fmtDate(j.fin)}</td>
                  <td>{calcularDuracion(j.duracionMs)}</td>

                  {/* Distancias con color por rango */}
                  <td className="col-distancia">
                    <div className="distancia-cell">
                      {j.latitud && j.longitud && j.distancia_m != null ? (
                        <button
                          className={`btn-mapa btn-mapa--chip ${j.en_rango ? "is-ok" : "is-ko"}`}
                          onClick={() => abrirMapa(j, "inicio")}
                          title={`Inicio · ${j.en_rango ? "En rango" : "Fuera de rango"}`}
                        >
                          Inicio: {j.distancia_m} m
                        </button>
                      ) : (
                        <span className="distancia-placeholder">Inicio: -</span>
                      )}

                      {j.latitud_cierre && j.longitud_cierre && j.distancia_m_cierre != null ? (
                        <button
                          className={`btn-mapa btn-mapa--chip ${
                            j.en_rango_cierre == null ? "is-muted" : j.en_rango_cierre ? "is-ok" : "is-ko"
                          }`}
                          onClick={() => abrirMapa(j, "cierre")}
                          title={`Cierre · ${
                            j.en_rango_cierre == null ? "Sin dato" : j.en_rango_cierre ? "En rango" : "Fuera de rango"
                          }`}
                        >
                          Cierre: {j.distancia_m_cierre} m
                        </button>
                      ) : (
                        <span className="distancia-placeholder">Cierre: -</span>
                      )}
                    </div>
                  </td>

                  {/* Validada (solo Sí/No) */}
                  <td className="col-validada">
                    <span className={`estado ${j.validada ? "ok" : "ko"}`}>{j.validada ? "Sí" : "No"}</span>
                  </td>

                  {/* Acciones */}
                  <td className="botones-usuario botones-usuario--jornadas col-acciones">
                    <div className="btn-row">
                      <button
                        className="btn-mapa btn--compact"
                        onClick={() => {
                          setJornadaEditar(j);
                          setModalEditar(true);
                        }}
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>

                      <button
                        className="btn-mapa btn--compact"
                        onClick={() => toggleValidada(j)}
                        title={j.validada ? "Quitar validación" : "Validar"}
                      >
                        {j.validada ? "Quitar validación" : "Validar"}
                      </button>
                    </div>
                  </td>

                  {/* Validado por (separado) */}
                  <td>
                    {j.validada ? (
                      <>
                        <div>{validadorNombre}</div>
                        <div style={{ color: "#555" }}>{fmtDate(j.validada_en)}</div>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>

                  {/* Resto de info de gestión */}
                  <td>{j.motivo_fin || "-"}</td>
                  <td>{j.creado_por_nombre || "-"}</td>
                  <td>{j.observaciones_admin || "-"}</td>
                </tr>
              );
            })}

            {pageItems.length === 0 && (
              <tr>
                <td colSpan={14} className="no-results">
                  No hay resultados con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Exportaciones */}
      <div className="export-bar">
        <button className="btn-mapa" onClick={() => exportarCSVReducido(jornadas, "todas_jornadas.csv")}>
          <FontAwesomeIcon icon={faFileCsv} className="btn-icon-left" /> CSV Todas
        </button>
        <button
          className="btn-mapa"
          onClick={() => exportarCSVReducido(jornadasOrdenadas, "jornadas_filtradas.csv")}
        >
          <FontAwesomeIcon icon={faFileCsv} className="btn-icon-left" /> CSV Filtradas
        </button>
        <button className="btn-mapa" onClick={() => exportarExcel(jornadas, "todas_jornadas.xlsx")}>
          <FontAwesomeIcon icon={faFileExcel} className="btn-icon-left" /> Excel Todas
        </button>
        <button className="btn-mapa" onClick={() => exportarExcel(jornadasOrdenadas, "jornadas_filtradas.xlsx")}>
          <FontAwesomeIcon icon={faFileExcel} className="btn-icon-left" /> Excel Filtradas
        </button>
      </div>

      <FingerprintCheckerJornadas />

      {/* MODAL DE EDICIÓN */}
      <ModalEditar
        show={modalEditar}
        onClose={() => {
          setModalEditar(false);
          setJornadaEditar(null);
        }}
        jornada={jornadaEditar}
        onGuardar={handleGuardarEdicion}
      />

      {mostrarMapa && (ubicacionInicio || ubicacionCierre) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button onClick={() => setMostrarMapa(false)} className="modal-close">
              &times;
            </button>
            <h3>Ubicación de la Jornada</h3>
            <MapContainer
              center={
                mapFocus === "cierre"
                  ? ubicacionCierre
                    ? [ubicacionCierre.lat, ubicacionCierre.lng]
                    : ubicacionInicio
                    ? [ubicacionInicio.lat, ubicacionInicio.lng]
                    : [0, 0]
                  : ubicacionInicio
                  ? [ubicacionInicio.lat, ubicacionInicio.lng]
                  : ubicacionCierre
                  ? [ubicacionCierre.lat, ubicacionCierre.lng]
                  : [0, 0]
              }
              zoom={15}
              style={{ height: "400px", width: "100%" }}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Satélite + Callejero">
                  <TileLayer
                    url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                    attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {ubicacionInicio && (
                <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                  <Tooltip direction="top" offset={[0, -10]}>📍 Jornada (inicio)</Tooltip>
                </Marker>
              )}

              {ubicacionCierre && (
                <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                  <Tooltip direction="top" offset={[0, -10]}>✅ Jornada (cierre)</Tooltip>
                </Marker>
              )}

              {ubicacionCentro && (
                <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                  <Tooltip direction="top" offset={[0, -10]}>🏢 Centro de trabajo</Tooltip>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};
